import React, { useEffect, useMemo, useRef, useState } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

function IconChevronDown() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="6 9 12 15 18 9" />
		</svg>
	);
}

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function TagBadge({ tag, onRemove }) {
	const color = tag.color || "#64748b";

	return (
		<button
			type="button"
			className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition"
			style={{
				backgroundColor: `${color}14`,
				color,
				borderColor: `${color}40`,
			}}
			onClick={() => onRemove(tag.id)}
			title={tag.name}
		>
			<span className="size-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
			<span className="truncate max-w-[10rem]">{tag.name}</span>
			<span className="text-[11px] opacity-80">x</span>
		</button>
	);
}

export function TagsFilter({ onFiltered, className = "", variant }) {
	const isTailwick = variant === "tailwick";
	const containerRef = useRef(null);
	const [open, setOpen] = useState(false);
	const [tags, setTags] = useState([]);
	const [selecteds, setSelecteds] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		const loadTags = async () => {
			try {
				const { data } = await api.get("/tags/list");
				setTags(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		};

		loadTags();
	}, []);

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	const filteredTags = useMemo(() => {
		const normalized = searchTerm.trim().toLowerCase();

		if (!normalized) {
			return tags;
		}

		return tags.filter((tag) => String(tag.name || "").toLowerCase().includes(normalized));
	}, [searchTerm, tags]);

	const selectedIds = selecteds.map((tag) => String(tag.id));
	const summary =
		selecteds.length === 0
			? isTailwick
				? i18n.t("contacts.filterTagsPlaceholder")
				: "Filtrar por tags"
			: selecteds.length <= 2
				? selecteds.map((tag) => tag.name).join(", ")
				: i18n.t("contacts.filterTagsSelectedCount", { count: selecteds.length });

	const notifyChange = (nextSelecteds) => {
		setSelecteds(nextSelecteds);
		if (typeof onFiltered === "function") {
			onFiltered(nextSelecteds);
		}
	};

	const handleToggle = (tag) => {
		const exists = selectedIds.includes(String(tag.id));

		notifyChange(
			exists
				? selecteds.filter((item) => String(item.id) !== String(tag.id))
				: [...selecteds, tag]
		);
	};

	const handleRemove = (tagId) => {
		notifyChange(selecteds.filter((item) => String(item.id) !== String(tagId)));
	};

	const handleClear = () => {
		notifyChange([]);
		setSearchTerm("");
	};

	const rootClass = ["w-full", isTailwick && "tags-filter-tailwick", className]
		.filter(Boolean)
		.join(" ");

	return (
		<div ref={containerRef} className={rootClass}>
			<div className="relative">
				<button
					type="button"
					className="form-input form-input-sm flex w-full items-center justify-between gap-3 text-left"
					onClick={() => setOpen((current) => !current)}
				>
					<span
						className={`truncate ${
							selecteds.length > 0
								? "text-default-900 dark:text-default-100"
								: "text-default-500 dark:text-default-400"
						}`.trim()}
					>
						{summary}
					</span>
					<IconChevronDown />
				</button>

				{open && (
					<div className="tags-filter-tailwick__dropdown absolute left-0 right-0 z-30 mt-2 rounded-xl border p-2 shadow-lg">
						<div className="relative mb-2">
							<input
								type="search"
								className="form-input form-input-sm w-full ps-8"
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
								placeholder={i18n.t("contacts.filterTagsPlaceholder")}
							/>
							<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
								<IconSearch />
							</div>
						</div>

						<div className="max-h-60 overflow-auto">
							{filteredTags.length > 0 ? (
								filteredTags.map((tag) => {
									const checked = selectedIds.includes(String(tag.id));
									const color = tag.color || "#64748b";

									return (
										<label
											key={tag.id}
											className="tags-filter-tailwick__option flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2"
										>
											<input
												type="checkbox"
												className="size-4 rounded border-default-300 text-primary focus:ring-primary"
												checked={checked}
												onChange={() => handleToggle(tag)}
											/>
											<span
												className="size-3 shrink-0 rounded-full"
												style={{ backgroundColor: color }}
											/>
											<span className="min-w-0 flex-1 truncate text-sm text-default-800 dark:text-default-100">
												{tag.name}
											</span>
										</label>
									);
								})
							) : (
								<p className="m-0 px-3 py-2 text-sm text-default-500 dark:text-default-400">
									{i18n.t("tags.empty")}
								</p>
							)}
						</div>

						{selecteds.length > 0 && (
							<div className="mt-2 border-t border-default-200 pt-2 dark:border-default-700">
								<button
									type="button"
									className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
									onClick={handleClear}
								>
									{i18n.t("contacts.filterTagsClear")}
								</button>
							</div>
						)}
					</div>
				)}
			</div>

			{selecteds.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-2">
					{selecteds.map((tag) => (
						<TagBadge key={tag.id} tag={tag} onRemove={handleRemove} />
					))}
				</div>
			)}
		</div>
	);
}
